import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * A like-for-like replica of the STANDARD Universal Login widget: the white
 * 400x540 card that the hosted (non-ACUL) screens draw.
 *
 * WHY THIS EXISTS INSTEAD OF ULThemeCard / ULThemePageLayout. The reset-password
 * screens are a VISUAL MIGRATION: members must not be able to tell the ACUL
 * version from the stock one (RO-A brief, Phase 1). The existing ACUL screens
 * deliberately don't look stock: signup uses a flat 480px journey column, and
 * the Flow B MFA screen uses the portal's panel. The shared ULTheme* field
 * components also differ from the hosted ones in measurable ways (56px vs
 * 52px inputs, their own focus rings and shadows). Restyling them would change
 * the signup screens too, which the brief rules out. So these screens get
 * their own small set of components, and every number here was MEASURED off
 * the live standard screens on 2026-10-13 (getComputedStyle on
 * auth.azuremark.co/u/reset-password/change and /u/reset-verify), not
 * estimated. See INVESTIGATION-ro-acul-reset.md and the baseline screenshots
 * in acul/baseline-screenshots/reset-password/.
 *
 * Measured standard widget: 400px wide, min-height 540px, radius 16px, shadow
 * 0 12px 40px rgba(0,0,0,.12), 40px side padding, centred on both axes.
 *
 * Colours, radii and font sizes come from the tenant theme via the
 * --ul-theme-* variables that applyAuth0Theme() sets, so a theme change in
 * auth0_branding_theme reaches these screens the same way it reaches the
 * hosted ones.
 */

// The hosted page's own font stack, verbatim. `ulp-font` is only defined when
// the theme has a font_url, and this tenant's is empty, so every browser
// resolves the same fallback chain the hosted screens do. Copying the stack
// rather than picking "the font it looks like" is what makes the two render
// identically on every device, not just this one.
export const UNIVERSAL_LOGIN_FONT_STACK =
  "ulp-font, -apple-system, BlinkMacSystemFont, Roboto, Helvetica, sans-serif";

interface UniversalLoginWidgetProps {
  children: ReactNode;
  className?: string;
}

// Content is centred VERTICALLY inside the 540px card on every screen, the
// form screen included. Measured: on /u/reset-password/change the 468px of
// content sits 36px below the card top, exactly (540 - 468) / 2.
export default function UniversalLoginWidget({
  children,
  className,
}: UniversalLoginWidgetProps) {
  return (
    <div
      className="min-h-screen w-full flex items-center justify-center min-[601px]:py-10"
      style={{
        backgroundColor: "var(--ul-theme-page-bg-background-color, #ffffff)",
        fontFamily: UNIVERSAL_LOGIN_FONT_STACK,
      }}
    >
      {/* Small screens use Auth0's own breakpoints, read from the hosted
       * stylesheet (main_wcag_compliant.cdn.min.css), not Tailwind's:
       *   <= 600px  no shadow, no radius, widget fills the page height
       *   500-600px still 400px wide
       *   <  500px  full width
       * Padding stays 40px throughout (measured at 493px). */}
      <main
        className={cn(
          "w-full min-h-screen flex flex-col justify-center",
          "min-[500px]:w-[400px]",
          "min-[601px]:min-h-[540px]",
          "min-[601px]:rounded-[var(--ul-theme-border-widget-corner-radius,16px)]",
          "min-[601px]:shadow-[0_12px_40px_rgba(0,0,0,0.12)]",
          className
        )}
        style={{
          backgroundColor: "var(--ul-theme-color-widget-background, #ffffff)",
        }}
      >
        {children}
      </main>
    </div>
  );
}
